import { FiltersType } from '@/api/types';
import { client } from '@/client';
import {
  AdminTask,
  AdminTaskPayload,
  AdminTasksResultWithCount,
  CreateAdminTaskMutation,
  DeleteAdminTaskMutation,
  UpdateAdminTaskMutation,
} from '@/client/admin-tasks';
import { MessageResponseModel } from '@/client/models';
import { Tag } from '@/client/tags';
import { GenericForbiddenApiError } from '@/hooks/query/types';
import {
  useMutation,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

const queryKey = 'adminTasks';
const singleQueryKey = 'adminTask';
const tagsQueryKey = 'adminTaskTags';

export const useAdminTaskTags = (
  params: UseQueryOptions = {},
): UseQueryResult<Tag[]> & {
  adminTaskTags: Tag[] | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [tagsQueryKey],
    () => client.adminTasks.getAdminTaskTags(),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    adminTaskTags: data,
    ...(rest as UseQueryResult<Tag[]>),
  };
};

export const useAdminTasks = (
  params: UseQueryOptions & {
    sort?: string[];
    filters?: FiltersType;
  } = {},
): UseQueryResult<AdminTasksResultWithCount> & {
  adminTasks: AdminTasksResultWithCount | undefined;
} => {
  const {
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [queryKey, JSON.stringify(sort), JSON.stringify(filters)],
    () => client.adminTasks.getAdminTasks({ sort, filters }),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    adminTasks: data,
    ...(rest as UseQueryResult<AdminTasksResultWithCount>),
  };
};

export const useAdminTask = (
  params: UseQueryOptions & { taskId: string },
): UseQueryResult<AdminTask> & {
  adminTask: AdminTask | undefined;
} => {
  const {
    taskId,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [singleQueryKey, taskId],
    () => client.adminTasks.getAdminTask(taskId),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      enabled: !!taskId,
      ...(restOptions as any),
    },
  );

  return {
    adminTask: data,
    ...(rest as UseQueryResult<AdminTask>),
  };
};

export const useCreateAdminTask = (): {
  create: (payload: AdminTaskPayload) => Promise<AdminTask>;
} & CreateAdminTaskMutation => {
  const { mutateAsync, ...rest } = useMutation<
    AdminTask,
    GenericForbiddenApiError,
    AdminTaskPayload
  >('createAdminTask', (payload: AdminTaskPayload) =>
    client.adminTasks.createAdminTask(payload),
  );

  return { create: mutateAsync, ...(rest as any) };
};

export const useUpdateAdminTask = (): {
  update: (
    payload: { taskId: string } & AdminTaskPayload,
  ) => Promise<AdminTask>;
} & UpdateAdminTaskMutation => {
  const { mutateAsync, ...rest } = useMutation<
    AdminTask,
    GenericForbiddenApiError,
    { taskId: string } & AdminTaskPayload
  >('updateAdminTask', (payload) => {
    const { taskId, ...request } = payload;
    return client.adminTasks.updateAdminTask(
      taskId,
      request as AdminTaskPayload,
    );
  });

  return { update: mutateAsync, ...(rest as any) };
};

export const useDeleteAdminTask = (): {
  delete: (payload: { taskId: string }) => Promise<MessageResponseModel>;
} & DeleteAdminTaskMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    { taskId: string }
  >('deleteAdminTask', (payload: { taskId: string }) =>
    client.adminTasks.deleteAdminTask(payload.taskId),
  );

  return { delete: mutateAsync, ...(rest as any) };
};
